// components/reports/ReportTable.jsx
import React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

const ReportTable = ({
  data,
  columns,
  groupings,
  aggregates,
  reportFormat,
  expandedGroups,
  onToggleGroup,
}) => {
  const { isDarkMode } = useTheme();

  if (!data || data.length === 0) {
    return (
      <div
        className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        No data to display
      </div>
    );
  }

  // Tabular format - simple table
  if (reportFormat === "tabular" || !groupings || groupings.length === 0) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr
              className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
            >
              {columns.map((col, index) => (
                <th
                  key={index}
                  className={`text-left px-4 py-3 text-sm font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={`border-b transition-colors ${
                  isDarkMode
                    ? "border-gray-700 hover:bg-gray-800"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                {columns.map((col, colIndex) => (
                  <td key={colIndex} className="px-4 py-3 text-sm">
                    {formatValue(row[col.field], col.type, isDarkMode)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Summary format - grouped with subtotals
  if (reportFormat === "summary") {
    return (
      <SummaryTable
        data={data}
        groupings={groupings}
        aggregates={aggregates}
        expandedGroups={expandedGroups}
        onToggleGroup={onToggleGroup}
        isDarkMode={isDarkMode}
      />
    );
  }

  // Matrix format - pivot table
  if (reportFormat === "matrix") {
    return (
      <MatrixTable
        data={data}
        groupings={groupings}
        aggregates={aggregates}
        isDarkMode={isDarkMode}
      />
    );
  }

  return null;
};

const SummaryTable = ({
  data,
  groupings,
  aggregates,
  expandedGroups,
  onToggleGroup,
  isDarkMode,
}) => {
  // Group data by first grouping field
  const groupField = groupings[0]?.field;
  const groupedData = {};

  data.forEach((row) => {
    const groupValue = row[groupField] || "Ungrouped";
    if (!groupedData[groupValue]) {
      groupedData[groupValue] = [];
    }
    groupedData[groupValue].push(row);
  });

  return (
    <div className="space-y-4">
      {Object.keys(groupedData).map((groupValue) => {
        const groupKey = `${groupField}-${groupValue}`;
        const isExpanded = expandedGroups.has(groupKey);
        const rows = groupedData[groupValue];

        // Calculate group totals
        const groupTotals = {};
        aggregates?.forEach((agg) => {
          const key = agg.label || `${agg.function}(${agg.field})`;
          groupTotals[key] = rows.reduce((sum, row) => {
            return sum + (parseFloat(row[key]) || 0);
          }, 0);
        });

        return (
          <div
            key={groupKey}
            className={`border rounded-lg overflow-hidden ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {/* Group Header */}
            <div
              onClick={() => onToggleGroup(groupKey)}
              className={`px-4 py-3 cursor-pointer transition-colors flex items-center justify-between ${
                isDarkMode
                  ? "bg-gray-800 hover:bg-gray-700"
                  : "bg-gray-100 hover:bg-gray-200"
              }`}
            >
              <div className="flex items-center gap-2">
                {isExpanded ? (
                  <ChevronDown
                    size={16}
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  />
                ) : (
                  <ChevronRight
                    size={16}
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  />
                )}
                <span
                  className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {groupValue}
                </span>
                <span
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  ({rows.length} {rows.length === 1 ? "record" : "records"})
                </span>
              </div>

              {/* Group Totals */}
              <div className="flex gap-4 text-sm">
                {Object.keys(groupTotals).map((key) => (
                  <div key={key}>
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                    >
                      {key}:{" "}
                    </span>
                    <span className="font-semibold">
                      {formatValue(groupTotals[key], "number", isDarkMode)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Group Detail Rows */}
            {isExpanded && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr
                      className={`border-b ${isDarkMode ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-100"}`}
                    >
                      {groupings.slice(1).map((grouping, index) => (
                        <th
                          key={index}
                          className={`text-left px-4 py-2 text-xs font-semibold ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {grouping.label}
                        </th>
                      ))}
                      {aggregates?.map((agg, index) => (
                        <th
                          key={index}
                          className={`text-left px-4 py-2 text-xs font-semibold ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {agg.label || `${agg.function}(${agg.field})`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className={`border-b transition-colors ${
                          isDarkMode
                            ? "border-gray-700 hover:bg-gray-800/30"
                            : "border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        {groupings.slice(1).map((grouping, colIndex) => (
                          <td key={colIndex} className="px-4 py-2 text-sm">
                            {formatValue(
                              row[grouping.field],
                              undefined,
                              isDarkMode,
                            )}
                          </td>
                        ))}
                        {aggregates?.map((agg, colIndex) => {
                          const key =
                            agg.label || `${agg.function}(${agg.field})`;
                          return (
                            <td key={colIndex} className="px-4 py-2 text-sm">
                              {formatValue(row[key], "number", isDarkMode)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const MatrixTable = ({ data, groupings, aggregates, isDarkMode }) => {
  if (groupings.length < 2 || !aggregates || aggregates.length === 0) {
    return (
      <div
        className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        Matrix format requires at least 2 groupings and 1 aggregate
      </div>
    );
  }

  const rowGroupField = groupings[0].field;
  const colGroupField = groupings[1].field;
  const aggregateField = aggregates[0];
  const aggregateKey =
    aggregateField.label ||
    `${aggregateField.function}(${aggregateField.field})`;

  // Get unique row and column values
  const rowValues = [
    ...new Set(data.map((row) => row[rowGroupField] || "Unknown")),
  ];
  const colValues = [
    ...new Set(data.map((row) => row[colGroupField] || "Unknown")),
  ];

  // Build matrix
  const matrix = {};
  rowValues.forEach((rowVal) => {
    matrix[rowVal] = {};
    colValues.forEach((colVal) => {
      matrix[rowVal][colVal] = 0;
    });
  });

  // Fill matrix with data
  data.forEach((row) => {
    const rowVal = row[rowGroupField] || "Unknown";
    const colVal = row[colGroupField] || "Unknown";
    const value = parseFloat(row[aggregateKey]) || 0;
    matrix[rowVal][colVal] += value;
  });

  const bgColor = isDarkMode ? "#0C0E12" : "#FFFFFF";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";
  const headerBg = isDarkMode ? "bg-gray-800" : "bg-gray-100";

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className={`border-b ${borderColor}`}>
            <th
              className={`text-left px-4 py-3 text-sm font-semibold sticky left-0 ${headerBg} ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
              style={{ backgroundColor: bgColor }}
            >
              {groupings[0].label}
            </th>
            {colValues.map((colVal, index) => (
              <th
                key={index}
                className={`text-right px-4 py-3 text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                {colVal}
              </th>
            ))}
            <th
              className={`text-right px-4 py-3 text-sm font-semibold ${headerBg} ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {rowValues.map((rowVal, rowIndex) => {
            const rowTotal = colValues.reduce(
              (sum, colVal) => sum + matrix[rowVal][colVal],
              0,
            );

            return (
              <tr
                key={rowIndex}
                className={`border-b transition-colors ${borderColor} ${
                  isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"
                }`}
              >
                <td
                  className={`px-4 py-3 text-sm font-medium sticky left-0 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                  style={{ backgroundColor: bgColor }}
                >
                  {rowVal}
                </td>
                {colValues.map((colVal, colIndex) => (
                  <td key={colIndex} className="px-4 py-3 text-sm text-right">
                    {formatValue(matrix[rowVal][colVal], "number", isDarkMode)}
                  </td>
                ))}
                <td
                  className={`px-4 py-3 text-sm text-right font-semibold ${headerBg}`}
                >
                  {formatValue(rowTotal, "number", isDarkMode)}
                </td>
              </tr>
            );
          })}
          {/* Column Totals Row */}
          <tr className={`border-t-2 ${headerBg} font-semibold`}>
            <td className={`px-4 py-3 text-sm sticky left-0 ${headerBg}`}>
              Total
            </td>
            {colValues.map((colVal, colIndex) => {
              const colTotal = rowValues.reduce(
                (sum, rowVal) => sum + matrix[rowVal][colVal],
                0,
              );
              return (
                <td key={colIndex} className="px-4 py-3 text-sm text-right">
                  {formatValue(colTotal, "number", isDarkMode)}
                </td>
              );
            })}
            <td className={`px-4 py-3 text-sm text-right ${headerBg}`}>
              {formatValue(
                rowValues.reduce((sum, rowVal) => {
                  return (
                    sum +
                    colValues.reduce(
                      (colSum, colVal) => colSum + matrix[rowVal][colVal],
                      0,
                    )
                  );
                }, 0),
                "number",
                isDarkMode,
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// Helper function to format values
const formatValue = (value, type, isDarkMode) => {
  if (value === null || value === undefined) return "-";

  if (type === "number" || typeof value === "number") {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  if (type === "currency") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  }

  if (type === "date") {
    return new Date(value).toLocaleDateString();
  }

  if (type === "datetime") {
    return new Date(value).toLocaleString();
  }

  return String(value);
};

export default ReportTable;
